import React from 'react'
import { Modal, Stepper } from '@enamelai/design-system'
import './PurchaseFaxStyle.css'
import { SelectNumber, ReviewInfo } from './Steps'
import usePurchaseFax from './usePurchaseFax'

type Props = {
  show: boolean
  clinicId: string
  onHide: () => void
  onAdded: () => void
}

const formatMoney = (value: number): string => `$${value.toFixed(2)}`

const PurchaseFaxModal = ({
  show,
  clinicId,
  onHide,
  onAdded,
}: Props): React.ReactElement => {
  const hooks = usePurchaseFax({ show, clinicId, onHide, onAdded })

  const getSectionComponent = (): React.ReactElement => {
    switch (hooks.step) {
      case 0:
        return (
          <SelectNumber
            numberType={hooks.numberType}
            setNumberType={hooks.setNumberType}
            availableNumbers={hooks.availableNumbers}
            isNumbersLoading={hooks.isNumbersLoading}
            fetchError={hooks.fetchError}
            selectedNumber={hooks.selectedNumber}
            setSelectedNumber={hooks.setSelectedNumber}
          />
        )
      case 1:
        return (
          <ReviewInfo
            phoneNumber={hooks.selectedNumber?.phoneNumber ?? ''}
            lineName={hooks.displayName}
            setLineName={hooks.setDisplayName}
            monthlyFee={formatMoney(hooks.monthlyAmount)}
            setupFee={formatMoney(hooks.setupAmount)}
            totalDue={formatMoney(hooks.monthlyAmount + hooks.setupAmount)}
          />
        )
      default:
        return <></>
    }
  }

  const isLastStep = hooks.step === hooks.steps.length - 1

  return (
    <Modal
      open={show}
      onClose={hooks.handleClose}
      closeOnBackdrop={false}
      className="scp-fax-purchase-modal"
      size="md"
      title="Purchase fax number"
      primaryLabel={isLastStep ? 'Proceed' : 'Next'}
      secondaryLabel={hooks.step === 0 ? 'Cancel' : 'Previous'}
      primaryDisabled={hooks.busy || (hooks.step === 0 && !hooks.canProceed)}
      onPrimaryClick={() => {
        if (hooks.busy) return
        if (isLastStep) {
          void hooks.handleConfirmProceed()
          return
        }
        if (!hooks.canProceed) return
        hooks.handleNext()
      }}
      onSecondaryClick={() => {
        if (hooks.busy) return
        if (hooks.step === 0) {
          hooks.handleClose()
          return
        }
        hooks.handlePrevious()
      }}
    >
      <Stepper steps={hooks.steps} currentStep={hooks.step} />
      <div className="scp-fax-step" key={hooks.step}>
        {getSectionComponent()}
      </div>
    </Modal>
  )
}

export default PurchaseFaxModal
